/**
 * 流程實例過濾器組件
 * 提供 workflow name, status, started date time 等過濾條件
 */

import React, { useState, useEffect } from 'react';
import { Form, Select, DatePicker, Button, Row, Col, Space, message, Segmented } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 獲取可用的工作流名稱列表
 * @returns {Promise<Array<string>>} 工作流名稱列表
 */
const fetchWorkflowNames = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return [];
    }

    // 獲取所有流程實例來提取唯一的工作流名稱
    const response = await fetch('/api/workflowexecutions/monitor?page=1&pageSize=1000', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    const executions = result.data || [];
    
    // 提取唯一的工作流名稱
    const workflowNames = [...new Set(executions.map(e => e.workflowName).filter(Boolean))];
    return workflowNames.sort();
  } catch (error) {
    console.error('獲取工作流名稱列表失敗:', error);
    return [];
  }
};

const InstanceFilters = ({ filters, onFiltersChange, onSearch, onReset, loading = false, showSearchButton = true, extraButtons = null, dateFilterType, onDateFilterTypeChange }) => {
  const { t } = useLanguage();
  const [form] = Form.useForm();
  const [workflowNames, setWorkflowNames] = useState([]);
  const [loadingWorkflowNames, setLoadingWorkflowNames] = useState(false);

  // 輔助函數：獲取翻譯，如果找不到則返回回退值
  const getTranslation = (key, fallback) => {
    const translated = t(key);
    return (translated && translated !== key) ? translated : fallback;
  };

  // 載入工作流名稱列表
  useEffect(() => {
    const loadWorkflowNames = async () => {
      setLoadingWorkflowNames(true);
      try {
        const names = await fetchWorkflowNames();
        setWorkflowNames(names);
      } catch (error) {
        console.error('載入工作流名稱失敗:', error);
        message.error('載入工作流名稱失敗');
      } finally {
        setLoadingWorkflowNames(false);
      }
    };

    loadWorkflowNames();
  }, []);

  // 當外部 filters 改變時，更新表單
  useEffect(() => {
    form.setFieldsValue({
      status: filters.status || 'all',
      workflowName: filters.workflowName || undefined,
      startDateRange: filters.startDateRange ? [
        dayjs(filters.startDateRange[0]).startOf('day'),
        dayjs(filters.startDateRange[1]).endOf('day'),
      ] : null
    });
  }, [filters, form]);

  // 處理表單值改變，立即更新過濾條件（用於實時響應選擇）
  const handleFormValuesChange = (changedValues, allValues) => {
    // 當工作流名稱、狀態或日期範圍改變時，立即更新過濾條件
    if (changedValues.workflowName !== undefined || changedValues.status !== undefined || changedValues.startDateRange !== undefined) {
      try {
        const newFilters = {
          ...filters,   // 保留 dateFilterType 等額外欄位
          status: allValues.status || 'all',
          workflowName: allValues.workflowName !== undefined && allValues.workflowName !== null ? String(allValues.workflowName) : '',
          startDateRange: allValues.startDateRange && Array.isArray(allValues.startDateRange) && allValues.startDateRange.length === 2
            ? [
                dayjs(allValues.startDateRange[0]).startOf('day').toISOString(),
                dayjs(allValues.startDateRange[1]).endOf('day').toISOString(),
              ]
            : null
        };
        console.log('[InstanceFilters] handleFormValuesChange 觸發，changedValues:', changedValues, 'allValues:', allValues, 'newFilters:', newFilters);
        onFiltersChange(newFilters);
      } catch (error) {
        console.error('處理表單值改變時出錯:', error);
        // 如果出錯，至少更新工作流名稱和狀態
        const newFilters = {
          ...filters,   // 保留 dateFilterType 等額外欄位
          status: allValues.status || 'all',
          workflowName: allValues.workflowName !== undefined && allValues.workflowName !== null ? String(allValues.workflowName) : '',
          startDateRange: null
        };
        console.log('[InstanceFilters] handleFormValuesChange 錯誤恢復，newFilters:', newFilters);
        onFiltersChange(newFilters);
      }
    }
  };

  const handleSearch = () => {
    form.validateFields().then(values => {
      const newFilters = {
        ...filters,   // 保留 dateFilterType 等額外欄位
        status: values.status || 'all',
        workflowName: values.workflowName || '',
        startDateRange: values.startDateRange ? [
          dayjs(values.startDateRange[0]).startOf('day').toISOString(),
          dayjs(values.startDateRange[1]).endOf('day').toISOString(),
        ] : null
      };
      
      onFiltersChange(newFilters);
      if (onSearch) {
        onSearch(newFilters);
      }
    }).catch(error => {
      console.error('表單驗證失敗:', error);
    });
  };

  const handleReset = () => {
    form.resetFields();
    if (onReset) {
      onReset();
      return;
    }
    const defaultFilters = {
      status: 'all',
      workflowName: '',
      startDateRange: null
    };
    onFiltersChange(defaultFilters);
    if (onSearch) {
      onSearch(defaultFilters);
    }
  };

  return (
    <Form
      form={form}
      layout="inline"
      style={{ marginBottom: '16px' }}
      initialValues={{
        status: 'all',
        workflowName: undefined,
        startDateRange: null
      }}
      onValuesChange={handleFormValuesChange}
    >
      <Row gutter={16} style={{ width: '100%' }}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label={t('reports.workflowName') || '工作流名稱'}
            name="workflowName"
          >
            <Select
              placeholder={t('reports.selectWorkflowName') || '選擇工作流名稱'}
              allowClear
              showSearch
              loading={loadingWorkflowNames}
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                // 直接觸發表單值改變處理
                const currentValues = form.getFieldsValue();
                handleFormValuesChange({ workflowName: value }, { ...currentValues, workflowName: value });
              }}
            >
              {workflowNames.map(name => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label={t('reports.status') || '狀態'}
            name="status"
          >
            <Select placeholder={t('reports.selectStatus') || '選擇狀態'}>
              <Option value="all">{t('reports.all') || '全部'}</Option>
              <Option value="running">{t('reports.running') || '運行中'}</Option>
              <Option value="completed">{t('reports.completed') || '已完成'}</Option>
              <Option value="failed">{t('reports.failed') || '失敗'}</Option>
              <Option value="waiting">{t('reports.waiting') || '等待中'}</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={7}>
          <Form.Item
            label={
              onDateFilterTypeChange ? (
                <Segmented
                  size="small"
                  value={dateFilterType || 'process'}
                  onChange={onDateFilterTypeChange}
                  options={[
                    { label: 'eForm 時間', value: 'eform' },
                    { label: '流程時間', value: 'process' },
                  ]}
                />
              ) : (t('reports.startedDateRange') || '開始時間範圍')
            }
            name="startDateRange"
          >
            <RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              placeholder={[
                t('reports.startDate') || '開始日期',
                t('reports.endDate') || '結束日期'
              ]}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={5}>
          <Form.Item label=" " colon={false}>
            <Space wrap size="small" style={{ width: '100%' }}>
              {showSearchButton && (
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                  size="middle"
                >
                  {getTranslation('common.search', '查詢')}
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="middle"
              >
                {getTranslation('common.reset', '重置')}
              </Button>
              {extraButtons}
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default InstanceFilters;
